import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  RemoveProposalGrantInput,
  RemoveProposalGrantMutation,
} from '@/gql/graphql';

const REMOVE_PROPOSAL_GRANT_MUTATION = graphql(`
  mutation RemoveProposalGrant($input: RemoveProposalGrantInput!) {
    removeProposalGrant(input: $input) {
      id
    }
  }
`);

/**
 * Remove an invoice grant from the Proposal. Removing one frees gross for the
 * grants attached after it, so every remaining grant's applied amount can
 * change — the caches are invalidated rather than patched.
 */
const useRemoveProposalGrant = (args?: {
  proposalId?: string;
  projectId?: string;
  options?: UseMutationOptions<
    RemoveProposalGrantMutation,
    unknown,
    RemoveProposalGrantInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: RemoveProposalGrantInput) => {
      return gqlClient.request(REMOVE_PROPOSAL_GRANT_MUTATION, { input });
    },
    onSuccess: async (data, variables, context) => {
      if (args?.proposalId) {
        await queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      if (args?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.profitBreakdown(args.projectId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useRemoveProposalGrant;
